import { useTranslation } from "react-i18next";

// The prototype keeps its own strings, so that it can go without touching the app's locale files
const en = {
  header: "Annual wheel",
  menuLabel: "Annual wheel (preview)",
  previousYear: "Previous year",
  nextYear: "Next year",
  today: "Today",
  done: "Done",
  openHint: "Select a review window to see its documents.",
  timelineHint: "Drag to move in time, or hold Ctrl or ⌘ and scroll to zoom.",
  zoomIn: "Zoom in",
  zoomOut: "Zoom out",
  wholeYear: "Whole year",
  wholeHistory: "Whole history",
  findDocument: "Find a document",
  showAllDocuments: "Show all documents",
  backToYear: (year: number) => `Back to ${year}`,
  documents: (count: number) =>
    `${count} ${count === 1 ? "document" : "documents"}`,
  documentUnit: (count: number): string =>
    count === 1 ? "document" : "documents",
  inReviewWindows: (count: number) =>
    `in ${count} review ${count === 1 ? "window" : "windows"}`,
  andMore: (amount: number) => `+${amount} more`,
  view: {
    wheel: "Wheel",
    line: "Timeline",
  },
  yearDescription: {
    wheel:
      "Each arc is the documents that start their review on the same day. The number is how many there are. A pointed end runs on into the next year; a notched start comes from the year before.",
    line: "Each bar is the documents that start their review on the same day. The number is how many there are. Earlier years show the reviews of the previous versions.",
  },
  documentsDescription: {
    wheel:
      "Each ring is one document, from the start of its review to the end. Each coloured leg is one workflow step and ends on its deadline.",
    line: "Each row is one document, from the start of its review to the end. Each coloured leg is one workflow step and ends on its deadline.",
  },
  documentHistoryDescription:
    "Each bar is one review of this document, one per version. Each coloured leg is one workflow step and ends on its deadline. Select a review to zoom in on it.",
  workflowStep: {
    numbered: (number: number) => `Step ${number}`,
    completed: "Completed",
  },
  events: {
    inYear: (year: number) => `Events in ${year}`,
    hint: "Each event is a pin on the wheel and on the timeline. Select one to change it.",
    none: "No events this year yet.",
    add: "Add event",
    edit: "Edit event",
    name: "Name",
    namePlaceholder: "Board meeting",
    date: "Date",
    color: "Colour",
  },
  eventColor: {
    blue: "Blue",
    purple: "Purple",
    pink: "Pink",
    red: "Red",
    yellow: "Yellow",
  },
  // The names of the made-up events, which follow the language
  meetings: {
    board: "Board meeting",
    audit: "Audit committee",
    risk: "Risk committee",
    remuneration: "Remuneration committee",
    annualGeneral: "Annual general meeting",
  },
};

export type MeetingKey = keyof typeof en.meetings;

const sv: typeof en = {
  header: "Årshjul",
  menuLabel: "Årshjul (förhandsvy)",
  previousYear: "Föregående år",
  nextYear: "Nästa år",
  today: "Idag",
  done: "Klar",
  openHint: "Välj en granskningsperiod för att se dess dokument.",
  timelineHint:
    "Dra för att flytta i tiden, eller håll Ctrl eller ⌘ och skrolla för att zooma.",
  zoomIn: "Zooma in",
  zoomOut: "Zooma ut",
  wholeYear: "Hela året",
  wholeHistory: "Hela historiken",
  findDocument: "Sök ett dokument",
  showAllDocuments: "Visa alla dokument",
  backToYear: (year) => `Tillbaka till ${year}`,
  documents: (count) => `${count} dokument`,
  documentUnit: () => "dokument",
  inReviewWindows: (count) =>
    `i ${count} granskningsperiod${count === 1 ? "" : "er"}`,
  andMore: (amount) => `+${amount} till`,
  view: {
    wheel: "Hjul",
    line: "Tidslinje",
  },
  yearDescription: {
    wheel:
      "Varje båge är de dokument som börjar granskas samma dag. Siffran visar hur många de är. En spetsig ände fortsätter in i nästa år; ett hack i början kommer från året innan.",
    line: "Varje stapel är de dokument som börjar granskas samma dag. Siffran visar hur många de är. Tidigare år visar granskningarna av de föregående versionerna.",
  },
  documentsDescription: {
    wheel:
      "Varje ring är ett dokument, från granskningens start till dess slut. Varje färgad del är ett steg i arbetsflödet och slutar vid stegets tidsfrist.",
    line: "Varje rad är ett dokument, från granskningens start till dess slut. Varje färgad del är ett steg i arbetsflödet och slutar vid stegets tidsfrist.",
  },
  documentHistoryDescription:
    "Varje stapel är en granskning av dokumentet, en per version. Varje färgad del är ett steg i arbetsflödet och slutar vid stegets tidsfrist. Välj en granskning för att zooma in på den.",
  workflowStep: {
    numbered: (number) => `Steg ${number}`,
    completed: "Slutfört",
  },
  events: {
    inYear: (year) => `Händelser ${year}`,
    hint: "Varje händelse är en nål på hjulet och på tidslinjen. Välj en för att ändra den.",
    none: "Inga händelser det här året ännu.",
    add: "Lägg till händelse",
    edit: "Redigera händelse",
    name: "Namn",
    namePlaceholder: "Styrelsemöte",
    date: "Datum",
    color: "Färg",
  },
  eventColor: {
    blue: "Blå",
    purple: "Lila",
    pink: "Rosa",
    red: "Röd",
    yellow: "Gul",
  },
  meetings: {
    board: "Styrelsemöte",
    audit: "Revisionsutskott",
    risk: "Riskutskott",
    remuneration: "Ersättningsutskott",
    annualGeneral: "Årsstämma",
  },
};

export const useText = () => {
  const { i18n } = useTranslation();

  return i18n.language === "sv" ? sv : en;
};
